import React, { useCallback, useMemo, useState } from 'react';
import ListView from './ListView';
import { SearchFilterBar } from './SearchFilterBar';
import { useTodos } from '../hooks/useTodos';
import { filterTodos } from '../App/utils/filterTodos';
import type { Todo } from '../types';
import { TaskStatus } from '../types';

export function AllTasksView() {
  const { todos, allTags, updateTodo } = useTodos();

  // Local filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [horizonFilter, setHorizonFilter] = useState<string[]>([]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'parent_only' | 'child_only'>('all');

  const filteredTodos = useMemo(() => filterTodos(todos, {
    searchQuery,
    statusFilter,
    horizonFilter,
    tagsFilter,
    taskTypeFilter,
  }), [todos, searchQuery, statusFilter, horizonFilter, tagsFilter, taskTypeFilter]);

  const handleTodoClick = useCallback((todo: Todo) => {
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  }, []);

  const handleStatusChange = useCallback((todoId: string, status: TaskStatus) => {
    updateTodo({ id: todoId, data: { status } });
  }, [updateTodo]);

  const toggleStatusFilter = useCallback((status: TaskStatus) => {
    setStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  }, []);

  const toggleHorizonFilter = useCallback((horizon: string) => {
    setHorizonFilter(prev => prev.includes(horizon) ? prev.filter(h => h !== horizon) : [...prev, horizon]);
  }, []);

  const toggleTagFilter = useCallback((tag: string) => {
    setTagsFilter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  }, []);

  const clearFilters = useCallback(() => {
    setStatusFilter([]);
    setHorizonFilter([]);
    setTagsFilter([]);
    setTaskTypeFilter('all');
    setSearchQuery('');
  }, []);

  const noop = useCallback(() => {}, []);

  return (
    <div className="h-full overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
      <div className="mb-4">
        <SearchFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(prev => !prev)}
          statusFilter={statusFilter}
          onStatusToggle={toggleStatusFilter}
          horizonFilter={horizonFilter}
          onHorizonToggle={toggleHorizonFilter}
          tagsFilter={tagsFilter}
          onTagToggle={toggleTagFilter}
          taskTypeFilter={taskTypeFilter}
          onTaskTypeChange={setTaskTypeFilter}
          allTags={allTags}
          onClearFilters={clearFilters}
        />
      </div>
      <ListView
        todos={filteredTodos}
        onTodoClick={handleTodoClick}
        onTodoStatusChange={handleStatusChange}
        onAddChild={noop}
        onMakeChild={noop}
        allTodos={todos}
      />
    </div>
  );
}
